import { validateTimesheets } from '../api/ocg'
import { recordDuplicateTimeEntryIds } from '../../../utils/duplicateEntry'
import type {
  TimesheetValidationPayload,
  TimesheetValidationResponse,
} from '../types'

const toDateString = (value: unknown): string => {
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10)
  }
  if (value instanceof Date) return value.toISOString().slice(0, 10)
  return new Date().toISOString().slice(0, 10)
}

const toHoursNumber = (value: unknown, isDurationInSeconds = false): number => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    const hours = isDurationInSeconds ? value / 3600 : value
    return Math.round(hours * 100) / 100
  }
  if (typeof value === 'string' && value && Number.isFinite(Number(value))) {
    const hours = isDurationInSeconds ? Number(value) / 3600 : Number(value)
    return Math.round(hours * 100) / 100
  }
  return 0
}

const getCode = (value: unknown): string | null => {
  const code =
    value && typeof value === 'object' && 'code' in value
      ? (value as { code?: unknown }).code
      : value
  if (code === null || code === undefined || code === '') return null
  return String(code)
}

const toNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined || value === '') return null
  if (typeof value === 'object') return null
  return String(value)
}

const toSourceId = (value: unknown): string | string[] | null => {
  if (Array.isArray(value)) {
    const ids = value
      .map(item => toNullableString(item))
      .filter((item): item is string => item !== null)
    return ids.length ? ids : null
  }

  return toNullableString(value)
}

const toApps = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter(Boolean).map(String)
  if (typeof value === 'string' && value) return [value]
  return []
}

const getRelatedSourceItem = (
  item: Record<string, any>
): Record<string, any> | null =>
  item?.relateTimeEntry ||
  item?.timeentry_data ||
  item?.timeEntry ||
  item?.time_entry ||
  null

const getFirstSlot = (
  item: Record<string, any>
): Record<string, any> | null => {
  if (!Array.isArray(item?.slots)) return null
  return item.slots.find(Boolean) ?? null
}

const pickSourceValue = (
  item: Record<string, any>,
  field: string
): string | string[] | null => {
  const slot = getFirstSlot(item)
  const related = getRelatedSourceItem(item)
  const candidates = [item?.[field], slot?.[field], related?.[field]]

  for (const candidate of candidates) {
    const value = toSourceId(candidate)
    if (value !== null) return value
  }

  return null
}

const getApps = (item: Record<string, any>): string[] => {
  const apps = toApps(item?.apps)
  if (apps.length) return apps
  const slotApps = toApps(getFirstSlot(item)?.apps)
  if (slotApps.length) return slotApps
  return toApps(getRelatedSourceItem(item)?.apps)
}

const getIsBillable = (item: Record<string, any>): boolean => {
  if (typeof item?.is_billable === 'boolean') return item.is_billable
  const slot = getFirstSlot(item)
  if (typeof slot?.is_billable === 'boolean') return slot.is_billable
  return true
}

const buildSharedTimesheetFields = (
  item: Record<string, any>,
  description: string
) => {
  const apps = getApps(item)

  return {
    client_id: String(item?.client_id ?? item?.client ?? ''),
    matter_id: String(item?.matter_id ?? item?.matter ?? ''),
    tkpr_id: String(item?.tkpr_id ?? ''),
    description,
    task_code: getCode(item?.task_code),
    activity_code: getCode(item?.activity_code),
    is_billable: getIsBillable(item),
    source_obj_id: pickSourceValue(item, 'source_obj_id'),
    source_event_id: pickSourceValue(item, 'source_event_id'),
    source_doc_id: pickSourceValue(item, 'source_doc_id'),
    source_conversation_id: pickSourceValue(item, 'source_conversation_id'),
    apps,
  }
}

export function buildTimesheetValidationPayload(
  item: Record<string, any>,
  description: string,
  narrativeOnly = true
): TimesheetValidationPayload {
  const hasExplicitHours = item?.hours !== null && item?.hours !== undefined
  const hours = toHoursNumber(
    hasExplicitHours ? item.hours : item?.duration,
    !hasExplicitHours
  )
  const id = item?.id
  const timesheet = buildSharedTimesheetFields(item, description)

  if (narrativeOnly) {
    return {
      narrative_only: true,
      skip_narrative_chk: false,
      timesheets: [timesheet],
    }
  }

  return {
    narrative_only: false,
    skip_narrative_chk: true,
    timesheets: [
      {
        ...timesheet,
        ...(id !== null && id !== undefined && id !== '' ? { id } : {}),
        work_date: toDateString(item?.work_date ?? item?.date ?? item?.start),
        hours,
        rate: item?.rate ?? null,
      },
    ],
  }
}

export async function validateTimesheetNarrative(
  item: Record<string, any>,
  description: string,
  narrativeOnly = true
): Promise<TimesheetValidationResponse> {
  const response = await validateTimesheets(
    buildTimesheetValidationPayload(item, description, narrativeOnly)
  )
  // Cache any multiple-attendee (duplicate) ids so the next create/patch/merge
  // payload can forward them to the backend (consume-once).
  recordDuplicateTimeEntryIds(response)
  return response
}
